import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  MessageSquare,
  Send,
  Key,
  AlertCircle,
  Save,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  RefreshCw,
  Webhook,
  Link2
} from 'lucide-react';
import { getSettings, updateSettings } from '../../../api/whatsapp';

const WhatsAppSettingsPage = () => {
  const location = useLocation();
  const isWholesalePath = location.pathname.startsWith('/wholesale');

  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showApiToken, setShowApiToken] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);

  const [activeModule, setActiveModule] = useState(isWholesalePath ? 'wholesale' : 'retail');

  // WhatsApp configuration settings
  const [config, setConfig] = useState({
    enabled: false,
    use_shared_settings: false,
    instance_id: '',
    api_token: '',
    webhook_secret: '',
    api_url: 'https://api.green-api.com',
    auto_parse_messages: true,
    auto_forward_orders: false,
    send_confirmation: true,
    send_delivery_updates: true,
    send_payment_reminders: true
  });

  // Message templates
  const [templates, setTemplates] = useState([
    {
      id: 1,
      name: 'Order Confirmation',
      type: 'order_confirmation',
      content:
        'Hello {{distributor_name}}, your order {{order_number}} has been confirmed. Total: ₦{{total_amount}}. Expected delivery: {{delivery_date}}.',
      active: true
    },
    {
      id: 2,
      name: 'Order Dispatched',
      type: 'order_dispatched',
      content:
        'Your order {{order_number}} has been dispatched and is on the way. Track your delivery with waybill: {{waybill_number}}.',
      active: true
    },
    {
      id: 3,
      name: 'Payment Reminder',
      type: 'payment_reminder',
      content:
        'Payment reminder for Invoice {{invoice_number}}. Amount due: ₦{{amount_due}}. Due date: {{due_date}}. Please arrange payment.',
      active: true
    },
    {
      id: 4,
      name: 'Order Delivered',
      type: 'order_delivered',
      content: 'Your order {{order_number}} has been delivered. Thank you for your business!',
      active: true
    }
  ]);

  // Webhook logs (mock data)
  const [webhookLogs] = useState([
    {
      id: 1,
      timestamp: '2025-12-28 10:30:45',
      type: 'incoming',
      from: '+2348012345678',
      status: 'success',
      message_preview: 'I need 50 boxes of...'
    },
    {
      id: 2,
      timestamp: '2025-12-28 10:25:12',
      type: 'outgoing',
      to: '+2348087654321',
      status: 'delivered',
      message_preview: 'Your order WHO-2025-001...'
    },
    {
      id: 3,
      timestamp: '2025-12-28 10:20:33',
      type: 'incoming',
      from: '+2348011122233',
      status: 'parsed',
      message_preview: 'Order: Product A - 20...'
    }
  ]);

  // Fetch settings when module changes
  React.useEffect(() => {
    fetchSettings(activeModule);
  }, [activeModule]);

  const fetchSettings = async (module) => {
    try {
      const data = await getSettings(module);
      setConfig((prev) => ({ ...prev, ...data }));
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const handleConfigChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    setError(null);

    try {
      // If using shared settings, inform the user
      if (config.use_shared_settings) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const sharedModule = activeModule === 'retail' ? 'Wholesale' : 'Retail';
        setTestResult({
          success: true,
          message: `Using shared credentials from ${sharedModule} module. Credentials are not tested directly here.`
        });
        return;
      }

      // Test the actual connection
      await new Promise((resolve) => setTimeout(resolve, 2000));

      if (config.instance_id && config.api_token) {
        setTestResult({
          success: true,
          message: 'Connection successful! (Simulated)'
        });
      } else {
        setTestResult({
          success: false,
          message: 'Connection failed. Please check your credentials.'
        });
      }
    } catch (err) {
      console.error('Error testing connection:', err);
      setTestResult({
        success: false,
        message: 'Connection test failed: ' + err.message
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage('');

    try {
      await updateSettings({ ...config, module: activeModule });

      setSuccessMessage(
        `${activeModule.charAt(0).toUpperCase() + activeModule.slice(1)} settings saved successfully!`
      );
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save WhatsApp settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">WhatsApp Configuration</h1>
        <p className="text-gray-500 mt-1">
          Configure WhatsApp integration for {activeModule} operations. You can use separate
          credentials for each module or share credentials between modules.
        </p>
      </div>

      {/* Module Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveModule('wholesale')}
            className={`${
              activeModule === 'wholesale'
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Wholesale Module
          </button>
          <button
            onClick={() => setActiveModule('retail')}
            className={`${
              activeModule === 'retail'
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Retail Module
          </button>
        </nav>
      </div>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          {successMessage}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      )}

      {/* Configuration Options Info */}
      <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-gray-900 mb-2">Configuration Options:</p>
            <ul className="space-y-1 text-gray-700">
              <li>
                <strong>Separate Credentials:</strong> Configure unique Green API credentials for
                each module (recommended for production)
              </li>
              <li>
                <strong>Shared Credentials:</strong> Use the same Green API instance for both
                modules (simpler setup, shared message pool)
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Enable/Disable WhatsApp */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">WhatsApp Integration Status</h2>
            <p className="text-sm text-gray-600">
              Enable or disable WhatsApp integration for {activeModule} module
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={(e) => handleConfigChange('enabled', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-14 h-8 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-6 after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-emerald-600"></div>
          </label>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={config.use_shared_settings}
              onChange={(e) => handleConfigChange('use_shared_settings', e.target.checked)}
              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <div>
              <p className="text-sm font-medium text-gray-900">
                Use {activeModule === 'retail' ? 'Wholesale' : 'Retail'} Credentials
              </p>
              <p className="text-xs text-gray-500">
                Share Green API credentials with the{' '}
                {activeModule === 'retail' ? 'Wholesale' : 'Retail'} module instead of configuring
                separate credentials.
              </p>
            </div>
          </label>
        </div>

        {!config.enabled && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
              <p className="text-sm text-yellow-700">
                WhatsApp integration is currently disabled. Enable it to receive and send messages
                via WhatsApp.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Green API Credentials */}
      {!config.use_shared_settings && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Key className="h-5 w-5 text-emerald-600" />
            Green API Credentials
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">API URL</label>
              <input
                type="text"
                value={config.api_url}
                onChange={(e) => handleConfigChange('api_url', e.target.value)}
                placeholder="https://api.green-api.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Instance ID</label>
              <input
                type="text"
                value={config.instance_id}
                onChange={(e) => handleConfigChange('instance_id', e.target.value)}
                placeholder="Enter your Green API instance ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Your WhatsApp instance identifier from Green API
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">API Token</label>
              <div className="relative">
                <input
                  type={showApiToken ? 'text' : 'password'}
                  value={config.api_token}
                  onChange={(e) => handleConfigChange('api_token', e.target.value)}
                  placeholder="Enter your Green API token"
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  type="button"
                  onClick={() => setShowApiToken(!showApiToken)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showApiToken ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Your API authentication token (keep this secure)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Webhook Secret</label>
              <div className="relative">
                <input
                  type={showWebhookSecret ? 'text' : 'password'}
                  value={config.webhook_secret}
                  onChange={(e) => handleConfigChange('webhook_secret', e.target.value)}
                  placeholder="Enter webhook secret for validation"
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  type="button"
                  onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showWebhookSecret ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Secret key for webhook signature validation
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleTestConnection}
                disabled={testing || !config.instance_id || !config.api_token}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Send className="h-4 w-4" />
                {testing ? 'Testing...' : 'Test Connection'}
              </button>
            </div>

            {testResult && (
              <div
                className={`p-4 rounded-lg flex items-center gap-2 ${testResult.success ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}
              >
                {testResult.success ? (
                  <CheckCircle className="h-5 w-5" />
                ) : (
                  <XCircle className="h-5 w-5" />
                )}
                <p className="text-sm">{testResult.message}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {config.use_shared_settings && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Link2 className="h-5 w-5 text-blue-600" />
            Shared Credentials Configuration
          </h2>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-900 mb-1">Using Shared Credentials</p>
              <p className="text-sm text-blue-700">
                This module is currently using Green API credentials from the{' '}
                <strong>{activeModule === 'retail' ? 'Wholesale' : 'Retail'}</strong> module. To
                configure separate credentials, uncheck the "Use{' '}
                {activeModule === 'retail' ? 'Wholesale' : 'Retail'} Credentials" option above.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Automation Settings */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <RefreshCw className="h-5 w-5 text-purple-600" />
          Automation Settings
        </h2>

        <div className="space-y-3">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={config.auto_parse_messages}
              onChange={(e) => handleConfigChange('auto_parse_messages', e.target.checked)}
              className="mt-1 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <div>
              <p className="text-sm font-medium text-gray-700">Auto-parse incoming messages</p>
              <p className="text-xs text-gray-500">
                Automatically extract order details from WhatsApp messages
              </p>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={config.auto_forward_orders}
              onChange={(e) => handleConfigChange('auto_forward_orders', e.target.checked)}
              className="mt-1 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <div>
              <p className="text-sm font-medium text-gray-700">Auto-forward verified orders</p>
              <p className="text-xs text-gray-500">
                Automatically forward confirmed orders to manufacturing companies
              </p>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={config.send_confirmation}
              onChange={(e) => handleConfigChange('send_confirmation', e.target.checked)}
              className="mt-1 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <div>
              <p className="text-sm font-medium text-gray-700">Send order confirmations</p>
              <p className="text-xs text-gray-500">
                Send confirmation messages when orders are created
              </p>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={config.send_delivery_updates}
              onChange={(e) => handleConfigChange('send_delivery_updates', e.target.checked)}
              className="mt-1 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <div>
              <p className="text-sm font-medium text-gray-700">Send delivery updates</p>
              <p className="text-xs text-gray-500">Notify customers of order status changes</p>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={config.send_payment_reminders}
              onChange={(e) => handleConfigChange('send_payment_reminders', e.target.checked)}
              className="mt-1 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <div>
              <p className="text-sm font-medium text-gray-700">Send payment reminders</p>
              <p className="text-xs text-gray-500">
                Automatically remind customers of pending payments
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* Message Templates */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-green-600" />
          Message Templates
        </h2>

        <div className="space-y-4">
          {templates.map((template) => (
            <div key={template.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-medium text-gray-900">{template.name}</h3>
                  <p className="text-xs text-gray-500">Type: {template.type}</p>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${template.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                >
                  {template.active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 font-mono">
                {template.content}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Variables: Use {'{{variable_name}}'} for dynamic content
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Webhook Logs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Webhook className="h-5 w-5 text-blue-600" />
          Recent Webhook Activity
        </h2>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Message
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {webhookLogs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {log.timestamp}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${log.type === 'incoming' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}
                    >
                      {log.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {log.from || log.to}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${log.status === 'success' || log.status === 'delivered' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}
                    >
                      {log.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                    {log.message_preview}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
        >
          <Save className="h-4 w-4" />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Info Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">Green API Setup Guide:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Create an account at green-api.com</li>
              <li>Create a new WhatsApp instance</li>
              <li>Copy your Instance ID and API Token</li>
              <li>
                Configure webhook URL: {window.location.origin}/api/{activeModule}
                /webhooks/whatsapp/incoming
              </li>
              <li>Test the connection using the button above</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSettingsPage;
